/**
 * Числа, даты и перечисления по правилам локали.
 *
 * Ручной `toFixed` давал десятичную ТОЧКУ в русском кабинете и разряды без
 * разделителя; Intl решает то же самое одинаково для обоих языков. Отдельно
 * важен неразрывный пробел: `Intl.NumberFormat('ru')` разделяет разряды именно
 * им, и строка с ним не сравнима в тестах и по-разному ложится в вёрстку —
 * поэтому приводим к обычному.
 *
 * Время — UTC и 24-часовое в обоих языках. Кабинет промысловый: `6:05 PM`
 * вместо `18:05` в судовых сводках читается как ошибка, а не как локаль.
 */
import type { Lang } from './types'

/** Что показываем вместо числа/даты, которых нет. */
export const DASH = '—'

const NUMBER_CACHE = new Map<string, Intl.NumberFormat>()
const DATE_CACHE = new Map<string, Intl.DateTimeFormat>()

function numberFormat(lang: Lang, digits?: number): Intl.NumberFormat {
  const key = `${lang}:${digits ?? 'auto'}`
  let fmt = NUMBER_CACHE.get(key)
  if (!fmt) {
    fmt = new Intl.NumberFormat(lang,
      digits === undefined
        ? { maximumFractionDigits: 3 }
        : { minimumFractionDigits: digits, maximumFractionDigits: digits },
    )
    NUMBER_CACHE.set(key, fmt)
  }
  return fmt
}

function dateFormat(lang: Lang, kind: 'day' | 'time' | 'dayTime' | 'month'): Intl.DateTimeFormat {
  const key = `${lang}:${kind}`
  let fmt = DATE_CACHE.get(key)
  if (!fmt) {
    const day: Intl.DateTimeFormatOptions =
      lang === 'ru'
        ? { day: '2-digit', month: '2-digit', year: 'numeric' }
        : { month: 'short', day: 'numeric', year: 'numeric' }
    const time: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }
    const options: Intl.DateTimeFormatOptions =
      kind === 'day' ? day
      : kind === 'time' ? time
      : kind === 'month' ? { month: 'long', year: 'numeric' }
      : { ...day, ...time }
    fmt = new Intl.DateTimeFormat(lang, { timeZone: 'UTC', ...options })
    DATE_CACHE.set(key, fmt)
  }
  return fmt
}

/** Неразрывные пробелы Intl → обычные (см. верхний комментарий). */
function plainSpaces(text: string): string {
  return text.replace(/[  ]/g, ' ')
}

/**
 * Число словами локали. `digits` — сколько знаков после запятой держать
 * (не указано → до трёх, лишние нули не дописываются). Не-число → тире.
 */
export function formatNumber(
  lang: Lang,
  value: number | null | undefined,
  digits?: number,
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH
  return plainSpaces(numberFormat(lang, digits).format(value))
}

function parseUtc(value: string | null | undefined): Date | null {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Дата без времени. Пусто → тире, мусор → как пришло (поломку не прячем). */
export function formatDay(lang: Lang, value: string | null | undefined): string {
  if (!value) return DASH
  const d = parseUtc(value)
  if (!d) return value
  return plainSpaces(dateFormat(lang, 'day').format(d))
}

/** Время UTC, 24-часовое. */
export function formatTime(lang: Lang, value: string | null | undefined): string {
  if (!value) return DASH
  const d = parseUtc(value)
  if (!d) return value
  return plainSpaces(dateFormat(lang, 'time').format(d))
}

/** Дата и время UTC одной строкой. */
export function formatDayTime(lang: Lang, value: string | null | undefined): string {
  if (!value) return DASH
  const d = parseUtc(value)
  if (!d) return value
  return plainSpaces(dateFormat(lang, 'dayTime').format(d))
}

/** Месяц и год («август 2026» / «August 2026»). */
export function formatMonth(lang: Lang, value: string | null | undefined): string {
  if (!value) return DASH
  const d = parseUtc(value)
  if (!d) return value
  return plainSpaces(dateFormat(lang, 'month').format(d))
}

/** Перечисление: «а, б и в» / «a, b, and c». */
export function formatList(lang: Lang, items: string[]): string {
  if (!items.length) return ''
  return plainSpaces(new Intl.ListFormat(lang, { style: 'long', type: 'conjunction' }).format(items))
}
